import { CONTACT_STYLES } from './contact-styles.js';
import { postContact, DEFAULT_API_BASE } from './post-contact.js';
import { fetchContactConfig } from './config-client.js';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function resolveTheme(attr) {
  if (attr === 'light' || attr === 'dark') return { mode: attr, auto: false };
  const mql = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;
  return { mode: mql && mql.matches ? 'dark' : 'light', auto: true, mql };
}

class AttraxionContactElement extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    const slug = this.getAttribute('venue');
    if (!slug) return; // nothing to do without a venue
    this._mounted = true;

    const apiBase = this.getAttribute('api') || DEFAULT_API_BASE;
    const colorAttr = this.getAttribute('color');
    const theme = resolveTheme(this.getAttribute('theme'));
    const root = this.attachShadow({ mode: 'open' });
    root.innerHTML = `
      <style>${CONTACT_STYLES}</style>
      <form class="ax-contact" data-theme="${theme.mode}" novalidate>
        <label>Name<input name="name" type="text" autocomplete="name" maxlength="190"></label>
        <label>Email<input name="email" type="email" autocomplete="email" required maxlength="190"></label>
        <label>Phone (optional)<input name="phone" type="tel" autocomplete="tel" maxlength="40"></label>
        <label>Booking reference (optional)<input name="bookingRef" type="text"></label>
        <label>Subject<input name="subject" type="text" maxlength="255"></label>
        <label>Message<textarea name="message" rows="4" required maxlength="10000"></textarea></label>
        <input class="ax-hp" name="website" type="text" tabindex="-1" autocomplete="off" aria-hidden="true">
        <button type="submit" class="ax-submit">Send message</button>
        <p class="ax-status" role="status" hidden></p>
      </form>`;

    const form = root.querySelector('form');

    // Brand colour: explicit attr wins; otherwise fetch the venue's config colour.
    if (colorAttr) {
      form.style.setProperty('--ax-color', colorAttr);
    } else {
      fetchContactConfig(slug).then((cfg) => {
        if (cfg && cfg.color) form.style.setProperty('--ax-color', cfg.color);
      });
    }

    // Auto theme: follow OS changes live.
    if (theme.auto && theme.mql && typeof theme.mql.addEventListener === 'function') {
      theme.mql.addEventListener('change', (e) => {
        form.setAttribute('data-theme', e.matches ? 'dark' : 'light');
      });
    }

    form.addEventListener('submit', (e) => this._onSubmit(e, slug, apiBase, form));
  }

  _status(form, text, kind) {
    const el = form.querySelector('.ax-status');
    el.textContent = text;
    el.hidden = false;
    el.setAttribute('data-kind', kind);
  }

  async _onSubmit(e, slug, apiBase, form) {
    e.preventDefault();
    if (this._submitting) return; // re-entrancy guard: ignore double-submit while in-flight
    const get = (n) => (form.querySelector(`[name="${n}"]`)?.value || '').trim();
    const email = get('email');
    const message = get('message');
    if (!EMAIL_RE.test(email) || !message) {
      this._status(form, 'Please enter a valid email and a message.', 'error');
      return;
    }
    const btn = form.querySelector('.ax-submit');
    this._submitting = true;
    btn.disabled = true;
    this._status(form, 'Sending…', 'pending');

    const payload = {
      name: get('name'), email, phone: get('phone'), subject: get('subject'),
      message, bookingRef: get('bookingRef'), website: get('website'),
    };
    const res = await postContact({ apiBase, slug, payload });
    this._submitting = false;
    btn.disabled = false;
    if (res.ok) {
      form.reset();
      this._status(form, "Thanks — we've received your message and will reply by email.", 'success');
    } else {
      this._status(form, 'Sorry, something went wrong. Please try again.', 'error');
    }
  }
}

if (!customElements.get('attraxion-contact')) {
  customElements.define('attraxion-contact', AttraxionContactElement);
}

export { AttraxionContactElement };
